import React, { useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import { AppConstants, TwilioApi, ChangelogApi } from "../../configs";
import { AxiosAuth, ChannelContext } from "../../core";
import { UtilsJQuery } from "../../utils";
import { APISchemaRelease, APISchemaChange, ChangelogChangeType, ApiSchemaReleasePagedResponseData, ApiSchemaVersionConfig } from '../../models/api';
import { AppRouteUi, VriApi } from "../../configs";
import { logger } from "../../utils";
import { onCLS } from "web-vitals";
import useSWR, { mutate } from "swr";
import {
  PaginationSettings,
  PlatformApi,
  topLoaderProgress,
} from "../../configs";
import {
  ApiResponse,
  ApiSchemaChangeStatus,
  ApiSchemaLanguageData,
  ApiSchemaUpdatePassword,
  ApiSchemaUpdateTin,
  Option,
  SelectModel,
  WithPagination,
} from "../../models";

type Props = {
  pageSize?: number;
  pageNo?: number;
  setLoaderProgress?: any;
};

let pagedReleasesUrl: string;

function getReleaseListFromResponse(payload: any): APISchemaRelease[] {
  const candidates = [
    payload?.data?.releaseResponse,
    payload?.data?.data?.releaseResponse,
    payload?.releaseResponse,
    payload?.data?.data,
    payload?.data,
    payload,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      return candidate;
    }
  }

  return [];
}

function getApiErrorMessage(e: any, fallback: string): string {
  const data = e?.response?.data;
  return data?.detail || data?.error || fallback;
}

function getReleaseCountFromResponse(payload: any): number {
  const candidates = [
    payload?.data?.count,
    payload?.data?.data?.count,
    payload?.count,
  ];

  for (const candidate of candidates) {
    if (typeof candidate === "number") {
      return candidate;
    }
  }

  return 0;
}

export function useFunctionalityPagedChangelog(props: Props) {
  const {
    setLoaderProgress,
    pageNo,
    pageSize,
  } = props;

  pagedReleasesUrl = useMemo(() => {
    return ChangelogApi.Changelog.GetPagedReleases(
      pageSize,
      pageNo
    );
  }, [pageSize, pageNo]);

  const { data: apiChangelogList, error, isLoading } = useSWR<any>(
    pagedReleasesUrl ? pagedReleasesUrl : null,
    async () => {
      setLoaderProgress?.(topLoaderProgress.start);
      try {
        const response = await AxiosAuth.get(
          pagedReleasesUrl
        );
        return response.data;
      } finally {
        setLoaderProgress?.(topLoaderProgress.complete);
      }
    }
  );

  if (error) {
    console.error(error);
    toast.error("Failed to fetch releases. Please try again.");
  }

  return {
    releaseList: getReleaseListFromResponse(apiChangelogList),
    pagedListCount: getReleaseCountFromResponse(apiChangelogList),
    pagedReleasesUrl,
    isLoading: isLoading || (!apiChangelogList && !error),
  };
}

export function useFunctionalityChangelog() {

  const onAddRelease = useCallback(
    async (form: APISchemaRelease) => {
      const btnLoading = UtilsJQuery.Ladda(".release-from-submit-btn");
      btnLoading.start?.();
      form = { ...form };
      try {
        const res = await AxiosAuth.post<APISchemaRelease>(
          ChangelogApi.Changelog.Create(),
          form
        );
        if (res.status === AppConstants.Api.StatusOK || res.status >= 200 && res.status < 300) {
          mutate(pagedReleasesUrl);
          toast.success("Release added successfully");
        }
      } catch (e) {
        toast.error(getApiErrorMessage(e, "Adding release failed"));
        btnLoading.stop?.();
        return Promise.reject(e);
      }
    },
    []
  );

  const onUpdateRelease = useCallback(async (releaseId: string, form: APISchemaRelease) => {
    if (!releaseId) {
      toast.error("Cannot update: release has no id (backend response is missing the id field)");
      return Promise.resolve();
    }
    const btnLoading = UtilsJQuery.Ladda(".release-from-submit-btn");
    btnLoading.start?.();

    try {
      // console.log("the received form for API is:", form);
      const res = await AxiosAuth.put<APISchemaRelease>(
        ChangelogApi.Changelog.Update(releaseId),
        form
      );

      if (res.status === AppConstants.Api.StatusOK || res.status >= 200 && res.status < 300) {
        mutate(pagedReleasesUrl);
        toast.success("Release Updated");
      }
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Release Update Failed"));
    }

    btnLoading.stop?.();
    return Promise.resolve();
  }, []);

  const onDeleteRelease = useCallback(async (releaseId: string) => {
    if (!releaseId) {
      toast.error("Cannot delete: release has no id (backend response is missing the id field)");
      return Promise.resolve();
    }
    const btnLoading = UtilsJQuery.Ladda(".release-from-delete-btn");
    btnLoading.start?.();

    try {
      const res = await AxiosAuth.delete(
        ChangelogApi.Changelog.Delete(releaseId)
      );

      if (res.status === AppConstants.Api.StatusOK || res.status >= 200 && res.status < 300) {
        mutate(pagedReleasesUrl);
        toast.success("Release Deleted");
      }
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Release Delete Failed"));
    }

    btnLoading.stop?.();
    return Promise.resolve();
  }, []);

  const getLatestRelease = useCallback(
    async (): Promise<string | null> => {

      try {
        const res = await AxiosAuth.get(
          ChangelogApi.Changelog.LatestVersion()
        );

        if (res.status === AppConstants.Api.StatusOK || res.status >= 200 && res.status < 300) {
          return res.data;
        }
      } catch (e: any) {
        toast.error(e.message);
      }
      return null;
    },
    []
  );

  const getReleasesCount = useCallback(
    async (): Promise<number | null> => {
      try {
        const res = await AxiosAuth.get(
          ChangelogApi.Changelog.GetAllReleases()
        );

        if (res.status === AppConstants.Api.StatusOK || res.status >= 200 && res.status < 300) {
          return res.data.count;
        }
      } catch (e: any) {
        toast.error(e.message);
      }
      return null;
    },
    []
  );

  return {
    onAddRelease,
    onUpdateRelease,
    onDeleteRelease,
    getLatestRelease,
    getReleasesCount
  };
}

export function useFunctionalityVersionConfig() {
  const getVersionConfig = useCallback(
    async (): Promise<ApiSchemaVersionConfig | null> => {
      try {
        const res = await AxiosAuth.get(
          ChangelogApi.Changelog.VersionConfig()
        );
        if (res.status >= 200 && res.status < 300 && res.data) {
          return res.data;
        }
      } catch (e) {
        toast.error(getApiErrorMessage(e, "Failed to load version config"));
      }
      return null;
    },
    []
  );

  const onUpdateVersionConfig = useCallback(
    async (form: ApiSchemaVersionConfig): Promise<boolean> => {
      const btnLoading = UtilsJQuery.Ladda(".version-config-submit-btn");
      btnLoading.start?.();
      try {
        const res = await AxiosAuth.put(
          ChangelogApi.Changelog.VersionConfig(),
          form
        );
        if (res.status >= 200 && res.status < 300) {
          toast.success("Version config updated");
          return true;
        }
      } catch (e) {
        toast.error(getApiErrorMessage(e, "Version config update failed"));
      } finally {
        btnLoading.stop?.();
      }
      return false;
    },
    []
  );

  return {
    getVersionConfig,
    onUpdateVersionConfig,
  };
}
